import { TFunction } from 'i18next';

import { truncateAtWordBoundary } from 'lib/strings/strings';
import {
  INQUISITIVE_FACEBOOK,
  INQUISITIVE_INSTAGRAM,
  INQUISITIVE_LEGAL_NAME_ABBREVIATED,
  INQUISITIVE_LINKEDIN,
  INQUISITIVE_SITE,
  INQUISITIVE_TWITTER,
  INQUISITIVE_TWITTER_CARD_TYPE,
  INQUISITIVE_TWITTER_HANDLE,
  INQUISITIVE_YOUTUBE,
  SUPPORT_INTERNATIONAL_NUMBER,
  INQUISITIVE_SITE_NAME,
  INQUISITIVE_INSTAGRAM_US,
  OPENGRAPH_IMAGE_SIZE,
  SOCIAL_IMAGE_SIZE,
  TWITTER_IMAGE_SIZE,
  INQUISITIVE_FACEBOOK_US,
  REGION_AU,
} from 'global/constants';
import { getYearsEducationRange } from 'approot/shared/logic/years';
import DefaultImagePathAu from 'approot/enterprise/inquisitive-au-thumbnail.jpg';
import DefaultImagePathUs from 'approot/enterprise/inquisitive-us-thumbnail.jpg';
import { MAX } from './helmet.constants';
import { SquareLandscapeImages } from 'approot/shared/graphql/responsive-images/square-landscape-images.constants';
import { getUnitLabel } from 'approot/shared/logic/content-label';
import { Regions } from 'approot/shared/region/region.constants';
import {
  LandscapeImageType,
  SquareImageType,
} from 'approot/shared/hero/hero-edit.constants';
import { getBaseUrl } from './helmet.utils';

let protocolDomainAndPort = `${window.location.protocol}//${
  window.location.hostname
}${
  window.location.port && window.location.port.toString() !== '80'
    ? `:${window.location.port}`
    : ''
}`;

// Ensuring that the merged url won't have too many, or too few, '/'s
if (
  protocolDomainAndPort.substr(protocolDomainAndPort.length - 1) !== '/' &&
  DefaultImagePathAu.substr(0, 1) !== '/' &&
  DefaultImagePathUs.substr(0, 1) !== '/'
) {
  protocolDomainAndPort += '/';
}

export const DEFAULT_TITLE =
  'Inquisitive | Enjoy teaching Science, History and Geography';

export const DEFAULT_TITLE_US = 'Inquisitive | Enjoy teaching NGSS Science';

type AnySection = undefined | null | string;

export const addRegionalSuffix = (title: string, region: Regions | string) => {
  return `${title} | ${region.toUpperCase()}`;
};

export const formatTitle = (
  region: Regions,
  ...anySections: AnySection[]
): string => {
  const sections = anySections.filter(section => !!section);

  const domainPrefix = getDomainPrefix();

  const title = truncateAtWordBoundary(
    sections ? sections.join(' | ') : '',
    MAX.TITLE
  );
  return sections?.length
    ? `${domainPrefix}${
        title.toUpperCase().endsWith(region.toUpperCase())
          ? title
          : addRegionalSuffix(title, region)
      }`
    : `${domainPrefix}${
        region === REGION_AU ? DEFAULT_TITLE : DEFAULT_TITLE_US
      }`;
};

export const getDomainPrefix = (domainOverride?: string): string => {
  // domainOverride used for testing
  const domain = domainOverride || window.location.hostname;
  if (domain === INQUISITIVE_SITE_NAME || process.env.NODE_ENV === 'test')
    return '';
  const domainPrefix = domain.split('.');
  return `${domainPrefix[0].toUpperCase()} `;
};

export type MetaTags = {
  title?: undefined | null | string;
  url?: undefined | null | string;
  name?: undefined | null | string;
  description?: undefined | null | string;
  shortDescription?: undefined | null | string;
  imageUrl?: undefined | null | string;
  responsiveImages?: undefined | null | SquareLandscapeImages;
  type?: undefined | null | string;
  robots?: undefined | null | string;
  includeHrefLang?: boolean;
  excludeCanonical?: boolean;
};

export const getImage = (
  responsiveImages: undefined | null | LandscapeImageType | SquareImageType,
  dimensions: number[],
  isRegionAU: boolean
) => {
  if (!responsiveImages) {
    // Default image
    return `${protocolDomainAndPort}${
      isRegionAU ? DefaultImagePathAu : DefaultImagePathUs
    }`;
  }

  return responsiveImages?.responsive?.find(
    (image: { width: number; height: number }) =>
      image.width === dimensions[0] && image.height === dimensions[1]
  )?.urls[1].url;
};

const defaultType = 'website';

/*
 * DEVELOPER NOTES
 *
 * There's a doc about meta tags here.
 * https://docs.google.com/spreadsheets/d/1MeJ0qcFFp_VkFO0TH1YMOvlJJFNqsZgFJjooFr7w5eE/edit#gid=0
 *
 */

export const standardMetaTags = (
  metaTags: undefined | null | MetaTags,
  t: TFunction,
  isRegionAU: boolean
) => {
  const unitsLabel = getUnitLabel(t, { lower: true, plural: true });
  const educationRange = getYearsEducationRange(t);

  let url,
    name,
    description,
    shortDescription,
    imageUrl,
    responsiveImages,
    type,
    robots;
  if (metaTags) {
    url = metaTags.url;
    name = metaTags.name;
    description = metaTags.description;
    // description char limit is shorter than twitter and OG description
    shortDescription = metaTags.shortDescription || metaTags.description;
    type = metaTags.type;
    imageUrl = metaTags.imageUrl;
    robots = metaTags.robots;
    responsiveImages = metaTags.responsiveImages;
  }

  const opengraphImage =
    imageUrl ||
    getImage(
      responsiveImages && responsiveImages?.landscape,
      OPENGRAPH_IMAGE_SIZE,
      isRegionAU
    );

  const twitterImage =
    imageUrl ||
    getImage(
      responsiveImages && responsiveImages?.landscape,
      TWITTER_IMAGE_SIZE,
      isRegionAU
    );
  const socialImage =
    imageUrl ||
    getImage(
      responsiveImages && responsiveImages?.square,
      SOCIAL_IMAGE_SIZE,
      isRegionAU
    );

  const chosenUrl = url || document.location.toString();
  const defaultDescription = isRegionAU
    ? `Inquisitive has everything you need to teach Science, History and Geography ${educationRange} and now adding English ${unitsLabel} F - 6 and Maths ${unitsLabel} F - 2.`
    : 'A rich, deep and flexible science curriculum with grades K - 2 now being added.';
  const defaultTitle = isRegionAU ? DEFAULT_TITLE : DEFAULT_TITLE_US;

  return [
    !metaTags?.excludeCanonical ? (
      <link
        key="key-canonical"
        rel="canonical"
        href={`${window.location.origin}${window.location.pathname}`}
      />
    ) : null,
    ...(metaTags?.includeHrefLang
      ? [
          <link
            key="alternate-x-default"
            rel="alternate"
            hrefLang="x-default"
            href={getBaseUrl('au', window.location.pathname)}
          />,
          <link
            key="alternate-en-au"
            rel="alternate"
            hrefLang="en-au"
            href={getBaseUrl('au', window.location.pathname)}
          />,
          <link
            key="alternate-en-us"
            rel="alternate"
            hrefLang="en-us"
            href={getBaseUrl('us', window.location.pathname)}
          />,
        ]
      : []),
    <meta
      property="name"
      name="name"
      content={
        (name && truncateAtWordBoundary(name, MAX.TITLE)) || defaultTitle
      }
      key="name"
    />,
    <meta
      property="description"
      name="description"
      content={
        (shortDescription &&
          truncateAtWordBoundary(shortDescription, MAX.DESCRIPTION)) ||
        defaultDescription
      }
      key="description"
    />,
    <meta name="image" property="og:image" content={socialImage} key="image" />,
    // Twitter
    <meta
      name="twitter:card"
      content={INQUISITIVE_TWITTER_CARD_TYPE}
      key="twitterCard"
    />,
    <meta
      name="twitter:site"
      content={`@${INQUISITIVE_TWITTER_HANDLE}`}
      key="twitterSite"
    />,
    <meta
      name="twitter:title"
      content={name || defaultTitle}
      key="twitterTitle"
    />,
    <meta
      name="twitter:description"
      content={
        (description &&
          truncateAtWordBoundary(description, MAX.TWITTER_DESCRIPTION)) ||
        defaultDescription
      }
      key="twitterDescription"
    />,
    <meta
      name="twitter:creator"
      content={INQUISITIVE_TWITTER_HANDLE}
      key="twitterCreator"
    />,
    <meta name="twitter:image" content={twitterImage} key="twitterImage" />,
    // Open Graph / Facebook
    <meta property="og:url" content={chosenUrl} key="ogUrl" />,
    <meta property="og:type" content={type || defaultType} key="ogType" />,
    <meta
      property="og:title"
      content={
        (name && truncateAtWordBoundary(name, MAX.TITLE)) || defaultTitle
      }
      key="ogTitle"
    />,
    <meta property="og:image" content={opengraphImage} key="ogImage" />,
    <meta
      property="og:description"
      content={
        (description &&
          truncateAtWordBoundary(description, MAX.OPENGRAPH_DESCRIPTION)) ||
        defaultDescription
      }
      key="ogDescription"
    />,
    <meta property="og:site_name" content="Inquisitive" key="ogSiteName" />,
    <meta
      name="telephone"
      content={SUPPORT_INTERNATIONAL_NUMBER}
      key="phoneNumber"
    />,
    robots ? <meta name="robots" content={robots} key="robots" /> : null,
    <meta property="fb:admins" content="inquisitivehq" key="fbAdmins" />,
    <script type="application/ld+json" key="ldJson">
      {JSON.stringify({
        '@context': 'http://schema.org',
        '@type': 'Organization',
        foundingDate: '2018',
        name: INQUISITIVE_LEGAL_NAME_ABBREVIATED,
        url: INQUISITIVE_SITE,
        author: INQUISITIVE_LEGAL_NAME_ABBREVIATED,
        sameAs: [
          INQUISITIVE_FACEBOOK,
          INQUISITIVE_FACEBOOK_US,
          INQUISITIVE_INSTAGRAM,
          INQUISITIVE_INSTAGRAM_US,
          INQUISITIVE_LINKEDIN,
          INQUISITIVE_TWITTER,
          INQUISITIVE_YOUTUBE,
        ],
      })}
    </script>,
  ];
};
